import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, Users, Lightbulb, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const UseCases: React.FC = () => {
    const { t } = useTranslation('marketing');

    const useCases = [
        { icon: Briefcase, titleKey: 'use_cases.executive_assistant.title', descKey: 'use_cases.executive_assistant.desc' },
        { icon: Users, titleKey: 'use_cases.customer_success.title', descKey: 'use_cases.customer_success.desc' },
        { icon: Lightbulb, titleKey: 'use_cases.expert_advisor.title', descKey: 'use_cases.expert_advisor.desc' },
        { icon: Calendar, titleKey: 'use_cases.team_coordinator.title', descKey: 'use_cases.team_coordinator.desc' },
    ];

    return (
        <section className="relative py-24 md:py-32 overflow-hidden" id="use-cases">
            {/* Static separator line */}
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-hig-accent/20 to-transparent" />

            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#00E5FF]/20 to-transparent" />

            <div className="container-hig relative">
                {/* Section header */}
                <motion.div
                    className="text-center mb-14"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-80px' }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                >
                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-white mb-4 tracking-tight">
                        {t('use_cases.title')}
                    </h2>
                    <p className="text-base md:text-lg text-white/40 max-w-2xl mx-auto">
                        {t('use_cases.subtitle')}
                    </p>
                </motion.div>

                {/* Use case grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
                    {useCases.map((useCase, index) => {
                        const IconComponent = useCase.icon;
                        return (
                            <motion.div
                                key={index}
                                className="relative group card-surface p-7 md:p-8 transition-colors"
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-60px' }}
                                transition={{ duration: 0.5, delay: index * 0.1, ease: [0.16, 1, 0.3, 1] }}
                                whileHover={{ borderColor: 'rgba(10,132,255,0.15)', transition: { duration: 0.3 } }}
                            >
                                {/* Hover glow */}
                                <motion.div
                                    className="absolute -inset-1 rounded-2xl pointer-events-none blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                                    style={{ background: 'radial-gradient(circle at 50% 50%, rgba(10,132,255,0.06), transparent 70%)' }}
                                />

                                {/* Icon */}
                                <div className="relative w-10 h-10 icon-box mb-5">
                                    <IconComponent className="w-5 h-5 text-hig-accent/70" />
                                </div>

                                {/* Title */}
                                <h3 className="relative text-base font-semibold text-white mb-3">
                                    {t(useCase.titleKey)}
                                </h3>

                                {/* Description */}
                                <p className="relative text-sm text-white/40 leading-relaxed">
                                    {t(useCase.descKey)}
                                </p>
                            </motion.div>
                        );
                    })}
                </div>

                {/* Human oversight note */}
                <motion.p
                    className="mt-10 text-center text-xs text-white/20"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                >
                    {t('use_cases.oversight_note')}
                </motion.p>
            </div>
        </section>
    );
};

export default UseCases;
